package org.teddapp.project.ui.focus

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import org.teddapp.project.ui.DecisionOption
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddDecision
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddSurface

/**
 * Fin de course : la course est déjà enregistrée. On propose une phrase sur ce qui a été
 * compris (auto-explication légère, Lombrozo), facultative : passer ne retire rien.
 * Deux gestes égaux ensuite : cocher l'élément, ou relancer la même durée.
 */
@Composable
internal fun FocusRunEnd(model: FocusModel) {
    val fr = model.finishedRun ?: return
    var text by remember(fr.rowId) { mutableStateOf("") }
    val itemTitle = fr.item?.let { model.resolveMeta(it).title }
    Dialog(onDismissRequest = { model.dismissRun() }, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .widthIn(max = 520.dp)
                .fillMaxWidth()
                .padding(16.dp)
                .teddSurface(shape = RoundedCornerShape(TeddTokens.RXl), elevation = 12.dp)
                // La largeur était bornée, pas la hauteur : la boîte grandissait avec
                // son contenu jusqu'à occuper tout l'écran. Elle s'arrête maintenant à
                // une hauteur de lecture, et ce qui dépasse défile à l'intérieur.
                .heightIn(max = 560.dp)
                .verticalScroll(rememberScrollState())
                .padding(22.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text(
                text = buildString {
                    append(if (fr.run.completed) "Course terminée" else "Course arrêtée")
                    append(" · ${fr.run.minutes} min")
                    if (itemTitle != null) append(" · $itemTitle")
                }.uppercase(),
                style = TeddType.Overline,
                color = TeddTokens.InkMuted,
            )
            Text("Une phrase sur ce que tu as compris ?", style = TeddType.Quote, color = TeddTokens.Ink)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(min = 96.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                    .padding(12.dp),
            ) {
                BasicTextField(
                    value = text,
                    onValueChange = { text = it },
                    textStyle = TeddType.BodyLg.copy(color = TeddTokens.Ink, fontSize = 15.sp, lineHeight = 22.sp),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    modifier = Modifier.fillMaxWidth(),
                    decorationBox = { inner ->
                        if (text.isEmpty()) {
                            Text("Ce que tu as compris, ou ce qui bloque encore…", color = TeddTokens.InkSubtle, style = TeddType.BodyLg, fontSize = 15.sp)
                        }
                        inner()
                    },
                )
            }
            SkillPicker(model, fr)
            Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Text("${text.trim().length} caractères", style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 12.sp)
                Text(
                    text = if (text.trim().length >= 80) "  ·  assez développé pour révéler ce qui manque" else "  ·  plus c'est précis, plus ça révèle ce qui manque",
                    color = TeddTokens.InkMuted,
                    fontSize = 12.sp,
                )
            }
            if (fr.item != null && fr.item.completed == 0L) {
                TeddDecision(
                    first = DecisionOption("Cocher l'élément", "il est fait"),
                    second = DecisionOption("Relancer ${model.timerMinutes} min", "même élément"),
                    onFirst = { if (text.isNotBlank()) model.saveRunNote(text) else model.dismissRun(); model.toggleItem(fr.item) },
                    onSecond = { if (text.isNotBlank()) model.saveRunNote(text); model.relaunchRun() },
                )
            }
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                TeddButton(
                    text = "Enregistrer",
                    onClick = { model.saveRunNote(text) },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Lg,
                    enabled = text.isNotBlank(),
                    modifier = Modifier.fillMaxWidth(),
                )
                TeddButton(
                    text = "Pause active d'une minute",
                    onClick = {
                        if (text.isNotBlank()) model.saveRunNote(text) else model.dismissRun()
                        model.bdnfOpen = true
                    },
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Md,
                    modifier = Modifier.fillMaxWidth(),
                )
                TeddButton(
                    text = "Passer, la course compte quand même",
                    onClick = { model.dismissRun() },
                    kind = TeddButtonKind.Ghost,
                    size = TeddButtonSize.Md,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}


/**
 * Choisir la compétence travaillée, sans noyer la question sous la liste.
 *
 * À trois compétences, des pastilles suffisent. À trente et une, elles remplissent
 * l'écran et repoussent hors de vue la phrase qu'on est venu écrire. Au-delà d'une
 * poignée, on cherche : les archivées sont écartées, et seules les correspondances
 * s'affichent.
 */
@Composable
private fun SkillPicker(model: FocusModel, fr: FinishedRun) {
    val actives = model.skills.filter { it.archived == 0L }
    if (actives.isEmpty()) return
    var query by remember { mutableStateOf("") }
    val choisie = actives.firstOrNull { it.id == fr.skillId }
    val q = query.trim().lowercase()

    // La compétence retenue reste visible même si la recherche l'exclut : sinon on ne
    // voit plus ce qu'on est en train de changer.
    val proposees = when {
        actives.size <= SKILL_PICKER_INLINE -> actives
        q.isEmpty() -> listOfNotNull(choisie)
        else -> actives.filter { it.name.lowercase().contains(q) }.take(SKILL_PICKER_MATCHES)
    }

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text("COMPÉTENCE TRAVAILLÉE", style = TeddType.Overline, color = TeddTokens.InkMuted)

        if (actives.size > SKILL_PICKER_INLINE) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = 12.dp, vertical = 9.dp),
                contentAlignment = Alignment.CenterStart,
            ) {
                BasicTextField(
                    value = query,
                    onValueChange = { query = it },
                    singleLine = true,
                    textStyle = TeddType.Body.copy(color = TeddTokens.Ink, fontSize = 13.sp),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    modifier = Modifier.fillMaxWidth(),
                    decorationBox = { inner ->
                        if (query.isEmpty()) {
                            Text(
                                text = if (choisie != null) "Chercher pour changer de compétence…" else "Chercher une compétence…",
                                color = TeddTokens.InkSubtle,
                                fontSize = 13.sp,
                            )
                        }
                        inner()
                    },
                )
            }
        }

        if (proposees.isEmpty()) {
            Text(
                text = if (q.isEmpty()) "Aucune compétence rattachée à cette course." else "Rien ne correspond.",
                color = TeddTokens.InkMuted,
                fontSize = 12.sp,
            )
        } else {
            FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                proposees.forEach { sk ->
                    val on = fr.skillId == sk.id
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RFull))
                            .background(if (on) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                                model.tagRunSkill(if (on) null else sk.id)
                                query = ""
                            }
                            .padding(horizontal = 11.dp, vertical = 6.dp),
                    ) {
                        Text(
                            text = sk.name,
                            color = if (on) TeddTokens.OnAction else TeddTokens.Ink,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                }
            }
        }
    }
}

/** Au-delà de ce nombre, on cherche au lieu de tout montrer. */
private const val SKILL_PICKER_INLINE = 8

/** Correspondances affichées : de quoi choisir sans remplir l'écran. */
private const val SKILL_PICKER_MATCHES = 8
